import { minutesToMilliseconds } from "../../common/utilities/number";
import { useCallback, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../app";
import {
  changeForceTokenReFetch,
  changeIsTokenRefreshing,
} from "../store/userSlice";
import { setFetchingErrorType } from "../../documentSet/store/documentSetSlice";
import useAuth, { isAlternativeFlow } from "../../authProvider/hooks/useAuth";
import { UNRECOVERABLE_AUTH0_ERRORS } from "../../authProvider/constants";

const INTERVAL = minutesToMilliseconds(10);

const useAccessTokenRefresh = (
  shouldRun: boolean,
  callbackRefresh: (
    accessToken: string,
    isTokenRefresh: boolean,
    authenticateUser: boolean,
  ) => Promise<void>,
  callbackLogout: () => void,
) => {
  const dispatch = useAppDispatch();

  const { isAuthenticated, getAccessTokenSilently } = useAuth();

  const forceTokenReFresh = useAppSelector(
    (state) => state.userReducer.forceTokenReFetch,
  );

  const handleUpdateAccessToken = useCallback(async () => {
    try {
      // The approuter renews the session on its own. A silent authorize against
      // Auth0 answers 403 here and blocks the SDK for its full timeout, so skip
      // it and let the `finally` below clear the flags right away.
      if (isAlternativeFlow) {
        return;
      }

      const accessToken = await getAccessTokenSilently();
      if (!accessToken) {
        callbackLogout();
        return;
      }

      await callbackRefresh(accessToken, true, true);

      dispatch(setFetchingErrorType(undefined));
    } catch (e) {
      const { error } = e as { error?: string };
      if (error && UNRECOVERABLE_AUTH0_ERRORS.includes(error)) {
        callbackLogout();
      }
    } finally {
      dispatch(changeForceTokenReFetch(false));
      dispatch(changeIsTokenRefreshing(false));
    }
  }, [dispatch, getAccessTokenSilently, callbackRefresh, callbackLogout]);

  useEffect(() => {
    if (!shouldRun) {
      return;
    }

    if (!isAuthenticated) {
      return;
    }

    const interval = setInterval(handleUpdateAccessToken, INTERVAL);

    return () => clearInterval(interval);
  }, [isAuthenticated, handleUpdateAccessToken, shouldRun]);

  useEffect(() => {
    if (!shouldRun) {
      return;
    }

    if (!forceTokenReFresh) {
      return;
    }

    if (!isAuthenticated) {
      // No session to refresh — drop the flag without triggering a logout cascade.
      dispatch(changeForceTokenReFetch(false));
      dispatch(changeIsTokenRefreshing(false));
      return;
    }

    handleUpdateAccessToken();
  }, [
    shouldRun,
    handleUpdateAccessToken,
    forceTokenReFresh,
    isAuthenticated,
    dispatch,
  ]);
};

export default useAccessTokenRefresh;
